import { atLeast } from './utils/breakpoints';
import videoSrc from '@videos/home-intro.mp4';
import audioOpus from '@audio/intro.webm';
import audioAac from '@audio/intro.m4a';

// Home intro (index.php #ofhome): the intro video (medium screens up), then the
// title, texts, logos, language switch and music fade in. Texts come in every
// language from the `conf` AJAX action; the language links switch between them.

const FADE_DELAY = 500;      // between the items fading in
const FADE_DURATION = 600;
const DIMMED = 0.3;          // video opacity behind the texts

const container = document.getElementById('ofhome');

const el = (tag, attrs = {}, ...children) => {
    const node = document.createElement(tag);
    Object.entries(attrs).forEach(([key, value]) => {
        if (key === 'style') Object.assign(node.style, value);
        else node.setAttribute(key, value);
    });
    node.append(...children);
    return node;
};

function introVideo(onDone) {
    const video = el('video', {preload: 'auto', style: {width: '100%', height: '100%'}});
    Object.assign(video, {src: videoSrc, muted: true, playsInline: true, autoplay: true});
    video.addEventListener('ended', onDone, {once: true});
    video.addEventListener('error', onDone, {once: true});
    video.play().catch(onDone);

    const wrapper = el('div', {style: {transition: `opacity ${FADE_DURATION}ms`}},
        el('div', {class: 'intro-video show-for-medium', style: {width: '100%', height: '100%'}}, video));
    return wrapper;
}

// Music toggle: starts on with the texts; if the browser blocks playback with
// sound (no interaction yet), the icon shows "off" and a click starts it.
function music() {
    const audio = el('audio', {id: 'intro-audio', loop: '', preload: 'none', style: {height: '0px', visibility: 'hidden'}},
        el('source', {src: audioOpus, type: 'audio/webm; codecs=opus'}),
        el('source', {src: audioAac, type: 'audio/mp4'}));
    const icon = el('span', {class: 'pointer glyphicon show-for-medium'});

    const show = (playing) => {
        icon.classList.toggle('glyphicon-volume-up', playing);
        icon.classList.toggle('glyphicon-volume-off', !playing);
    };
    const play = () => audio.play().then(() => show(true), () => show(false));

    icon.addEventListener('click', () => (audio.paused ? play() : (audio.pause(), show(false))));
    show(true);

    return {audio, icon, play};
}

// Children fade and slide in one after another (was react-fade-in).
function fadeIn(items) {
    const wrappers = items.map((item) => el('div', {
        style: {
            transition: `opacity ${FADE_DURATION}ms, transform ${FADE_DURATION}ms`,
            transform: 'translateY(20px)',
            opacity: 0,
        },
    }, item));

    wrappers.forEach((wrapper, i) => setTimeout(() => {
        Object.assign(wrapper.style, {transform: 'none', opacity: 1});
    }, FADE_DELAY * (i + 1)));

    return el('div', {}, ...wrappers);
}

function content(conf, text) {
    const t = (i) => conf.strings[i][conf.currentLang] ?? '';
    const subtitle = el('h2', {class: 'h6'}, t(0));
    const lead = el('p', {}, t(1));
    const start = el('a', {href: `${conf.site}/${conf.currentLang}/intro`, class: 'link button'}, t(2));
    const fullscreen = el('div', {class: 'micro h6 show-for-large'}, t(3));
    const {audio, icon, play} = music();

    const setLanguage = (lang) => {
        conf.currentLang = lang;
        subtitle.textContent = t(0);
        lead.textContent = t(1);
        start.textContent = t(2);
        start.href = `${conf.site}/${lang}/intro`;
        fullscreen.textContent = t(3);
    };

    const controls = el('div', {class: 'text-right intro-controls h6'},
        ...Object.values(conf.languages).map((l) => {
            const link = el('span', {title: l.name}, l.slug);
            link.addEventListener('click', () => setLanguage(l.slug));
            return link;
        }),
        icon);

    text.append(controls, fadeIn([
        el('div', {class: 'intro-title'}, el('h1', {}, 'Obliterated', el('br'), 'Families'), subtitle),
        lead,
        start,
        el('div', {class: 'row intro-logos intro-awards'}, ...conf.logos.map((src) => el('img', {src}))),
        el('div', {class: 'row intro-logos intro-partners'},
            ...conf.partners.map((p) => el('a', {href: p.url, target: '_blank'}, el('img', {src: p.file})))),
        fullscreen,
    ]));
    container.append(audio);
    play();
}

fetch(container.dataset.ajax + '?action=conf', {credentials: 'same-origin'})
    .then((response) => response.json())
    .then((conf) => {
        const text = el('div', {class: 'intro-text text-center'});
        let shown = false;
        let video = null;

        const showContent = () => {
            if (shown) return;
            shown = true;
            if (video) video.style.opacity = DIMMED;
            content(conf, text);
        };

        // Phones: the video is hidden there, so the texts come right away.
        if (atLeast('medium')) {
            video = introVideo(showContent);
            container.append(video);
        }
        container.append(text);
        if (!video) showContent();
    });
